import { Fragment, useState } from "react"
import { Badge, Card, CardBody, Col, Label, Progress, Row, Table } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import ThemeModal from "components/Common/ThemeModal"
import apiClient from "api/axios"
import { useEmployeeKpiMonthlyReport } from "hooks/useEmployeeKpiMonthlyReport"
import { useEmployeeKpiBreakdown } from "hooks/useEmployeeKpiBreakdown"

const METHODOLOGY_METRICS = [
  {
    module: "উপস্থিতি",
    items: [
      {
        name: "উপস্থিতির হার",
        basis: "সেই মাসে কর্মীর কার্যদিবস (সাপ্তাহিক ছুটি ও সরকারি ছুটি বাদে) এবং তার মধ্যে সে কতদিন উপস্থিত ছিল",
        formula: "উপস্থিত থাকা দিনসংখ্যা ÷ মোট কার্যদিবস × ১০০",
        na: "সেই মাসে তার কোনো কার্যদিবস না থাকলে (যেমন মাস শেষ হওয়ার আগেই যোগদান তারিখ পড়লে) — প্রযোজ্য নয় (N/A)",
      },
      {
        name: "সময়মতো উপস্থিতির হার",
        basis: "যেসব দিন সে উপস্থিত ছিল, তার মধ্যে কতদিন নির্ধারিত সময়ের মধ্যে এসেছে (দেরি করেনি)",
        formula: "সময়মতো উপস্থিত দিনসংখ্যা ÷ মোট উপস্থিত দিনসংখ্যা × ১০০",
        na: "সেই মাসে সে একদিনও উপস্থিত না থাকলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "টাস্ক",
    items: [
      {
        name: "টাস্ক সম্পন্ন করার হার",
        basis: "সেই মাসে কর্মীকে যতগুলো টাস্ক দেওয়া হয়েছিল এবং তার নির্ধারিত সময়সীমা সেই মাসের মধ্যে ছিল",
        formula: "সম্পন্ন করা টাস্কের সংখ্যা ÷ মোট দেওয়া টাস্কের সংখ্যা × ১০০",
        na: "সেই মাসে তাকে কোনো টাস্ক দেওয়া না হলে — প্রযোজ্য নয় (N/A)",
      },
      {
        name: "সময়মতো সম্পন্ন করার হার",
        basis: "সেই মাসে সম্পন্ন করা টাস্কগুলো কবে শেষ হয়েছে, তার সাথে নির্ধারিত সময়সীমার তুলনা",
        formula: "নির্ধারিত সময়ের মধ্যে সম্পন্ন করা টাস্ক ÷ মোট সম্পন্ন করা টাস্ক × ১০০",
        na: "সেই মাসে সে কোনো টাস্ক সম্পন্ন না করলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "ডেইলি ভেহিকেল চেক",
    items: [
      {
        name: "অংশগ্রহণ",
        basis: "যে দিনগুলোতে সে দৈনিক গাড়ি চেক-লিস্ট জমা দিয়েছে বা সম্পন্ন করেছে",
        formula: "তার সক্রিয় দিনসংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট সক্রিয় দিনসংখ্যা × ১০০ (অর্থাৎ মোট অংশগ্রহণের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই কাজে অংশ না নিলে — প্রযোজ্য নয় (N/A)",
        note: "এখন শুধু দিনভিত্তিক অংশগ্রহণ মাপা হয় — কোন নির্দিষ্ট গাড়ি সে নিজে চেক করেছে তা এখনো আলাদাভাবে ধরা যায় না।",
      },
    ],
  },
  {
    module: "ফুয়েল",
    items: [
      {
        name: "ফুয়েল ব্যবহারের হিসাব দেওয়া",
        basis: "সেই মাসে সে যতগুলো ফুয়েল ব্যবহারের হিসাব (এন্ট্রি) দিয়েছে",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
        note: "এটা কম ফুয়েল খরচ করা বোঝায় না — এটা নিয়মিত হিসাব দেওয়ার অভ্যাস মাপে।",
      },
      {
        name: "ফুয়েল কেনার হিসাব দেওয়া",
        basis: "সেই মাসে সে যতগুলো ফুয়েল কেনার হিসাব (এন্ট্রি) দিয়েছে",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "এক্সেসরিজ",
    items: [
      {
        name: "স্টকের হিসাব দেওয়া",
        basis: "সেই মাসে এক্সেসরিজ কেনার যতগুলো হিসাব দিয়েছে",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
      },
      {
        name: "ব্যবহারের হিসাব দেওয়া",
        basis: "সেই মাসে এক্সেসরিজ ব্যবহারের যতগুলো হিসাব দিয়েছে",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "বিল",
    items: [
      {
        name: "বিলের হিসাব দেওয়া",
        basis: "সেই মাসে বিভিন্ন ধরনের বিল (পেটি ক্যাশ, সার্ভিস, ভেন্ডর) মিলিয়ে সে যতগুলো হিসাব দিয়েছে",
        formula: "তার মোট বিল-এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট বিল-এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট বিলের কত শতাংশ সে একা দিয়েছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো বিলের হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "বিআরটিএ",
    items: [
      {
        name: "রেজিস্ট্রেশনের হিসাব দেওয়া",
        basis: "সেই মাসে গাড়ির বিআরটিএ কাগজপত্র নিবন্ধনের যতগুলো হিসাব দিয়েছে",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো এই হিসাব না দিলে — প্রযোজ্য নয় (N/A)",
        note: "গাড়ির কাগজের মেয়াদ শেষ হওয়ার তারিখ এখনো সিস্টেমে যুক্ত নেই, তাই এখন শুধু হিসাব দেওয়া হয়েছে কিনা তা মাপা হয় — সময়মতো করা হয়েছে কিনা তা এখনো মাপা যায় না।",
      },
    ],
  },
  {
    module: "ইনভেন্টরি",
    items: [
      {
        name: "ইনভেন্টরির হিসাব দেওয়া",
        basis: "সেই মাসে চূড়ান্তভাবে জমা দেওয়া (খসড়া বাদে) ইনভেন্টরি হিসাবের সংখ্যা",
        formula: "তার এন্ট্রি সংখ্যা ÷ এই কাজে যুক্ত সব কর্মীর মোট এন্ট্রি সংখ্যা × ১০০ (অর্থাৎ মোট কাজের কত শতাংশ সে একা করেছে)",
        na: "গত তিন মাস ও চলতি মাসে কখনো জমা না দিলে — প্রযোজ্য নয় (N/A)",
      },
    ],
  },
  {
    module: "উপস্থিতি লিডারবোর্ড",
    items: [
      {
        name: "লিডারবোর্ড র‍্যাংক",
        basis: "সম্পূর্ণ কম্পোজিট KPI স্কোর নয় — শুধু \"উপস্থিতি\" মডিউলের দুটি মেট্রিক (উপস্থিতির হার ও সময়মতো উপস্থিতির হার) নিয়ে আলাদাভাবে হিসাব করা একটি \"উপস্থিতি স্কোর\"",
        formula: "(উপস্থিতির হারের স্কোর × তার গুরুত্ব + সময়মতো উপস্থিতির হারের স্কোর × তার গুরুত্ব) ÷ (দুই মেট্রিকের গুরুত্বের যোগফল) — একই শোরুম ও একই মাসের সব কর্মীকে এই স্কোর অনুযায়ী বড় থেকে ছোট ক্রমে সাজিয়ে র‍্যাংক দেওয়া হয়",
        na: "যে মেট্রিক প্রযোজ্য নয় তা গড় থেকে বাদ যায়; দুটি মেট্রিকই প্রযোজ্য না হলে সেই কর্মী পুরো লিডারবোর্ড থেকেই বাদ যান",
        note: "সম্পূর্ণ তালিকা দেখতে \"উপস্থিতি লিডারবোর্ড দেখুন\" অনুমতি লাগে — এই অনুমতি ছাড়া প্রতিটি কর্মী শুধু শীর্ষ ৩ জন ও নিজের র‍্যাংক দেখতে পান।",
      },
    ],
  },
]

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" },
]

const buildYearOptions = () => {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => current - 2 + i)
}

const scoreColor = score => {
  if (score === null || score === undefined) return "secondary"
  if (score >= 80) return "success"
  if (score >= 60) return "warning"
  return "danger"
}

const KpiMethodologyModal = ({ isOpen, toggle }) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const downloadMethodologyPdf = async () => {
    setIsDownloading(true)
    try {
      const res = await apiClient.get("/kpi/methodology/pdf", { responseType: "blob" })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "kpi-score-methodology.pdf")
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("KPI methodology PDF download failed", err)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="KPI স্কোর কিভাবে হিসাব করা হয়?"
      size="lg"
      keyboard
      footer={
        <button
          type="button"
          className="btn btn-outline-success"
          onClick={downloadMethodologyPdf}
          disabled={isDownloading}
        >
          <i className="bx bx-download me-1"></i>
          {isDownloading ? "ডাউনলোড হচ্ছে…" : "PDF ডাউনলোড করুন"}
        </button>
      }
    >
    <p className="mb-3">
      প্রতি মাসের KPI স্কোর তৈরি হয় কর্মী সরাসরি কতটা কাজে অংশ নিয়েছে তার ভিত্তিতে —
      নিচের ৮টি মডিউলের মোট ১২টি মেট্রিক্স মিলিয়ে। যেসব মেট্রিক্স একজন কর্মীর কাজের সাথে
      প্রাসঙ্গিকই নয় (যেমন তার কাজ কখনো বিআরটিএ স্পর্শ করে না), সেগুলো তার স্কোরে <strong>N/A</strong>{" "}
      হিসেবে বাদ যায় — ০ হিসেবে গণনা হয় না।
    </p>

    {METHODOLOGY_METRICS.map(group => (
      <div key={group.module} className="mb-4">
        <h6 className="fw-bold text-primary mb-2">{group.module}</h6>
        {group.items.map(item => (
          <div key={item.name} className="mb-3 ps-2 border-start border-3">
            <div className="fw-semibold">{item.name}</div>
            <div className="small text-muted">
              <span className="fw-semibold text-body">যার ওপর ভিত্তি করে: </span>
              {item.basis}
            </div>
            <div className="small text-muted">
              <span className="fw-semibold text-body">সূত্র: </span>
              {item.formula}
            </div>
            <div className="small text-muted">
              <span className="fw-semibold text-body">N/A নিয়ম: </span>
              {item.na}
            </div>
            {item.note && (
              <div className="small text-warning-emphasis mt-1">
                <span className="fw-semibold">বিঃদ্রঃ </span>
                {item.note}
              </div>
            )}
          </div>
        ))}
      </div>
    ))}

    <hr />

    <h6 className="fw-bold mb-2">চূড়ান্ত (কম্পোজিট) স্কোর যেভাবে হয়</h6>
    <p className="small text-muted mb-1">
      প্রতিটি প্রযোজ্য (applicable) মেট্রিকের ওয়েট (weight) আছে, যা এডমিন পরিবর্তন করতে পারেন
      (মেট্রিক ওয়েটস পেজ থেকে)। একজন কর্মীর যে মেট্রিকগুলো প্রযোজ্য শুধু সেগুলোর ওয়েট দিয়ে
      ওয়েটেড এভারেজ নেওয়া হয় — অর্থাৎ কারো ১০টির মধ্যে ৩টি মেট্রিক N/A হলে, বাকি ৭টির
      ওয়েট দিয়েই তার স্কোর হিসাব হয়, N/A থাকার কারণে স্কোর কমে যায় না।
    </p>
    <p className="small text-muted mb-0">
      চলতি মাস (এখনো শেষ হয়নি) দেখলে স্কোর <Badge color="info">Live</Badge> — প্রতিবার
      লাইভ রিক্যালকুলেট হয়। মাস শেষ হওয়ার পর ১ তারিখে স্বয়ংক্রিয়ভাবে{" "}
      <Badge color="success">Final</Badge> স্ন্যাপশট নেওয়া হয়, যা পরে ওয়েট বদলালেও অপরিবর্তিত থাকে।
    </p>
    </ThemeModal>
  )
}

const BreakdownPanel = ({ employeeId, month, year }) => {
  const { data, isLoading } = useEmployeeKpiBreakdown(employeeId, month, year)
  const metrics = data?.data?.metrics ?? []

  if (isLoading) {
    return <div className="text-muted small py-2">Loading breakdown…</div>
  }

  if (!metrics.length) {
    return <div className="text-muted small py-2">No metric data for this period.</div>
  }

  return (
    <Table size="sm" borderless className="mb-0">
      <thead>
        <tr className="text-muted small">
          <th>Metric</th>
          <th className="text-center">Weight</th>
          <th className="text-center">Raw Value</th>
          <th className="text-center">Peer Avg</th>
          <th className="text-center">Score</th>
        </tr>
      </thead>
      <tbody>
        {metrics.map(metric => (
          <tr key={metric.key}>
            <td>{metric.name}</td>
            <td className="text-center">{metric.weight}</td>
            <td className="text-center">
              {metric.is_applicable ? metric.raw_value : "—"}
            </td>
            <td className="text-center">
              {metric.is_applicable && metric.peer_average !== null
                ? metric.peer_average
                : "—"}
            </td>
            <td className="text-center" style={{ minWidth: 140 }}>
              {metric.is_applicable ? (
                <div className="d-flex align-items-center gap-2">
                  <Progress
                    value={metric.normalized_score ?? 0}
                    color={scoreColor(metric.normalized_score)}
                    style={{ height: 6, flex: 1 }}
                  />
                  <span className="small">{metric.normalized_score}</span>
                </div>
              ) : (
                <Badge color="secondary" pill>N/A</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

const EmployeeKpiMonthlyReport = () => {
  document.title = "Employee KPI Report"

  const today = new Date()
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [year, setYear] = useState(today.getFullYear())
  const [yearOptions] = useState(buildYearOptions())
  const yearSelectOptions = yearOptions.map(y => ({ value: y, label: String(y) }))
  const [expandedId, setExpandedId] = useState(null)
  const [isMethodologyOpen, setIsMethodologyOpen] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)

  const { data, isLoading, isFetching, error, refetch } = useEmployeeKpiMonthlyReport(month, year)
  const report = data?.data
  const rows = report?.rows ?? []

  const downloadEmployeePdf = async row => {
    setDownloadingId(row.id)
    try {
      const res = await apiClient.get(`/kpi/employees/${row.id}/breakdown/pdf`, {
        params: { month, year },
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `employee-kpi-${row.employee_id ?? row.id}-${year}-${String(month).padStart(2, "0")}.pdf`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("KPI PDF download failed", err)
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <PageWrapper title="Employee KPI Report">
      <KpiMethodologyModal
        isOpen={isMethodologyOpen}
        toggle={() => setIsMethodologyOpen(prev => !prev)}
      />

      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-end">
            <Col md={2}>
              <Label>Month</Label>
              <Select
                options={MONTHS}
                value={MONTHS.find(m => m.value === month) || null}
                onChange={selected => setMonth(selected ? selected.value : month)}
                placeholder="Search month…"
              />
            </Col>
            <Col md={2}>
              <Label>Year</Label>
              <Select
                options={yearSelectOptions}
                value={yearSelectOptions.find(y => y.value === year) || null}
                onChange={selected => setYear(selected ? selected.value : year)}
                placeholder="Search year…"
              />
            </Col>
            <Col md={2}>
              <button className="btn btn-primary" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? "Refreshing…" : "Refresh"}
              </button>
            </Col>
            <Col md={3}>
              <button
                type="button"
                className="btn btn-outline-info"
                onClick={() => setIsMethodologyOpen(true)}
              >
                <i className="bx bx-info-circle me-1"></i>
                কিভাবে হিসাব হয়?
              </button>
            </Col>
            {report && (
              <Col className="text-end">
                {report.is_current_month && (
                  <Badge color="info" className="me-2">Live (in-progress month)</Badge>
                )}
                <span className="text-muted small">
                  Average score: {report.average_score ?? "—"}
                </span>
              </Col>
            )}
          </Row>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 small">
              Failed to load KPI report. {error?.response?.data?.message ?? error?.message}
            </div>
          )}
        </CardBody>
      </Card>

      {isLoading && !report && (
        <div className="text-center text-muted py-4">Loading KPI report…</div>
      )}

      {report && (
        <Card>
          <CardBody className="p-0">
            <div style={{ overflowX: "auto" }}>
              <Table responsive bordered className="mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 180 }}>Employee</th>
                    <th className="text-center">Composite Score</th>
                    <th className="text-center">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <Fragment key={row.id}>
                      <tr>
                        <td>
                          {row.name}
                          <br />
                          <span className="text-muted small">{row.employee_id}</span>
                        </td>
                        <td className="text-center">
                          {row.composite_score !== null ? (
                            <Badge color={scoreColor(row.composite_score)} pill>
                              {row.composite_score}
                            </Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge color={row.is_final ? "success" : "info"}>
                            {row.is_final ? "Final" : "Live"}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                            >
                              {expandedId === row.id ? "Hide" : "Breakdown"}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => downloadEmployeePdf(row)}
                              disabled={downloadingId === row.id}
                            >
                              {downloadingId === row.id ? "..." : "PDF"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedId === row.id && (
                        <tr>
                          <td colSpan={4} className="bg-light">
                            <BreakdownPanel employeeId={row.id} month={month} year={year} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No KPI data for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}
    </PageWrapper>
  )
}

export default EmployeeKpiMonthlyReport
